import { useEffect, useState } from 'react'
import { importLibrary, setOptions, type APIOptions } from '@googlemaps/js-api-loader'

const googleMapsApiConfig: APIOptions = {
  key: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  libraries: ['places', 'geocoding'],
}

type GoogleApiLoaderState = {
  isLoaded: boolean
  loadError?: Error
}

const initialLoaderState: GoogleApiLoaderState = {
  isLoaded: false,
}

let sharedLoaderState = initialLoaderState
let sharedLoaderPromise: Promise<void> | null = null
let didSetGoogleMapsOptions = false
let activeLoadId = 0

const subscribers = new Set<(state: GoogleApiLoaderState) => void>()

const normalizeLoadError = (error: unknown) => {
  if (error instanceof Error) {
    return error
  }

  return new Error('Unable to load the Google API.')
}

const notifySubscribers = () => {
  subscribers.forEach((subscriber) => subscriber(sharedLoaderState))
}

const loadGoogleApi = () => {
  if (sharedLoaderState.isLoaded) {
    return Promise.resolve()
  }

  if (!sharedLoaderPromise) {
    const loadId = ++activeLoadId

    if (!didSetGoogleMapsOptions) {
      setOptions(googleMapsApiConfig)
      didSetGoogleMapsOptions = true
    }

    sharedLoaderPromise = Promise.all([
      importLibrary('places'),
      importLibrary('geocoding'),
    ])
      .then(() => {
        if (loadId !== activeLoadId) {
          return
        }

        sharedLoaderState = {
          isLoaded: true,
        }
        notifySubscribers()
      })
      .catch((error) => {
        if (loadId !== activeLoadId) {
          return
        }

        sharedLoaderState = {
          isLoaded: false,
          loadError: normalizeLoadError(error),
        }
        sharedLoaderPromise = null
        notifySubscribers()
        throw sharedLoaderState.loadError
      })
  }

  return sharedLoaderPromise
}

export function useGoogleApiLoader() {
  const [loaderState, setLoaderState] = useState(sharedLoaderState)

  useEffect(() => {
    const handleStateChange = (nextState: GoogleApiLoaderState) => {
      setLoaderState(nextState)
    }

    const startLoading = () => {
      loadGoogleApi().catch(() => undefined)
    }

    subscribers.add(handleStateChange)
    setLoaderState(sharedLoaderState)
    startLoading()

    return () => {
      subscribers.delete(handleStateChange)
    }
  }, [])

  return loaderState
}
